import { useMutation, useQuery, useQueryClient, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { TResponse, TResponseWithQData } from "../../../types/common";
import {
  IDbsCheck, IDbsCreatePayload, IRtwCheck, IRtwCreatePayload,
  listDbsChecks, createDbsCheck, updateDbsCheck, deleteDbsCheck,
  listRtwChecks, createRtwCheck, updateRtwCheck, deleteRtwCheck,
} from "./callbacks";

// ── DBS ──────────────────────────────────────────────────────────────────────

export const useDbsList = (
  qData: QData
): UseQueryResult<TResponseWithQData<IDbsCheck[]>, Error> & { refetchWithQuery: (p: Partial<QData>) => void } => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<IDbsCheck[]>, Error>({
    queryKey: [API_ENDPOINTS.DBS_LIST, { ...queryParams, total: null }],
    queryFn: async () => (await listDbsChecks(queryParams)).data,
    select: (res) => ({ ...res, data: res.data.map((x) => ({ key: x.id, ...x })), qData: { ...queryParams, total: res.extra?.total } }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000, initialDataUpdatedAt: 0, refetchOnWindowFocus: false,
  });
  return { ...queryResult, refetchWithQuery: (p) => setQueryParams(p as QData) };
};

export const useDbsCreate = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IDbsCheck>, unknown, IDbsCreatePayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await createDbsCheck(data)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.DBS_LIST] }); onSuccess?.(); },
    onError,
  });
};

export const useDbsUpdate = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IDbsCheck>, unknown, IDbsCreatePayload & { id: number }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await updateDbsCheck(data)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.DBS_LIST] }); onSuccess?.(); },
    onError,
  });
};

export const useDbsDelete = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<null>, unknown, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await deleteDbsCheck(id)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.DBS_LIST] }); onSuccess?.(); },
    onError,
  });
};

// ── RTW ──────────────────────────────────────────────────────────────────────

export const useRtwList = (
  qData: QData
): UseQueryResult<TResponseWithQData<IRtwCheck[]>, Error> & { refetchWithQuery: (p: Partial<QData>) => void } => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });
  const queryResult = useQuery<TResponseWithQData<IRtwCheck[]>, Error>({
    queryKey: [API_ENDPOINTS.RTW_LIST, { ...queryParams, total: null }],
    queryFn: async () => (await listRtwChecks(queryParams)).data,
    select: (res) => ({ ...res, data: res.data.map((x) => ({ key: x.id, ...x })), qData: { ...queryParams, total: res.extra?.total } }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000, initialDataUpdatedAt: 0, refetchOnWindowFocus: false,
  });
  return { ...queryResult, refetchWithQuery: (p) => setQueryParams(p as QData) };
};

export const useRtwCreate = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IRtwCheck>, unknown, IRtwCreatePayload> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await createRtwCheck(data)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.RTW_LIST] }); onSuccess?.(); },
    onError,
  });
};

export const useRtwUpdate = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<IRtwCheck>, unknown, IRtwCreatePayload & { id: number }> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => (await updateRtwCheck(data)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.RTW_LIST] }); onSuccess?.(); },
    onError,
  });
};

export const useRtwDelete = ({
  onSuccess, onError,
}: { onSuccess?: () => void; onError?: (e: any) => void }): UseMutationResult<TResponse<null>, unknown, number> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await deleteRtwCheck(id)).data,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.RTW_LIST] }); onSuccess?.(); },
    onError,
  });
};
